/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useSelector, shallowEqual, useStore } from 'react-redux';
import { update } from '../../services/item';
import { deepCloneArray } from '../../utils/array';
import { processNum, processNumData, regexformula } from '../../utils/num';
import { canEditItemForItem, getId } from '../../utils/item';
import { buildTimelineEntryLabel } from '../../utils/timeline';
import { ModalGlobal } from '../modal/modal.Global';
import { NumBig } from './num.Big';
import { NumGauge } from './num.Gauge';
import { NumLittle } from './num.Little';
import { NumLittleGauge } from './num.LittleGauge';
import { NumSettings } from './num.Settings';
import { TooltipHtml } from '../tooltip/tooltip.Html';
import { TextMd } from '../text/text.Md';
import { useBoardDrag } from '../../context/boardDrag';
import { FaRegClock } from 'react-icons/fa';
import { useBoardReadOnlyMode } from '../../context/boardReadOnly';

export const NumGlobal = props => {
  const { numId, itemId, num, fieldInGrid, canEdit: canEditProp } = props;
  const modalId = `numSettings:${itemId}:${numId}`;
  const inheritedBoardReadOnlyMode = useBoardReadOnlyMode();

  const item = useSelector(state => state.items[itemId]);
  const store = useStore();
  const items = useSelector(
    state => {
      const allItems = state.items || {};
      const currentItem = allItems[itemId];
      if (!currentItem) {
        return {};
      }

      const formula = typeof num?.formula === 'string' ? num.formula : '';
      const requiresGlobalItems = formula.includes('[filter.');
      if (requiresGlobalItems) {
        return allItems;
      }

      const mapped = {};
      const add = id => {
        if (!id || mapped[id] || !allItems[id]) {
          return;
        }
        mapped[id] = allItems[id];
      };

      add(itemId);
      add(currentItem?.campaign);
      add(currentItem?.world);
      add(currentItem?.universe);

      if (Array.isArray(currentItem?.links)) {
        currentItem.links.forEach(linkObj => {
          add(getId(linkObj));
        });
      }

      if (Array.isArray(currentItem?.tabs)) {
        currentItem.tabs.forEach(tabObj => {
          const tabId = getId(tabObj);
          add(tabId);
          const tab = allItems[tabId];
          if (!Array.isArray(tab?.grid)) {
            return;
          }
          tab.grid.forEach(gridObj => {
            add(getId(gridObj));
          });
        });
      }

      const tokens = formula.split(regexformula);
      tokens.forEach(token => {
        const cleaned = token.replaceAll('[', '').replaceAll(']', '').trim();
        if (!cleaned.includes('.')) {
          return;
        }
        const parts = cleaned
          .split('.')
          .map(part => part.trim())
          .filter(Boolean);
        if (parts.length < 2) {
          return;
        }
        const itemRef = parts[0];
        if (/^[a-fA-F0-9]{24}$/.test(itemRef)) {
          add(itemRef);
        }
      });

      return mapped;
    },
    shallowEqual
  );
  const canEdit =
    inheritedBoardReadOnlyMode === 'hard'
      ? false
      : canEditProp ?? canEditItemForItem(item, items);
  const isMobile = useSelector(state => state.global.isMobile);
  const {
    dragData,
    startDrag,
    endDrag,
    isEnabled: isBoardDragEnabled,
  } = useBoardDrag();
  const dragPointerIdRef = useRef(null);

  const isDebugMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'debug';
  }, []);

  const computedNumData = useMemo(() => {
    if (typeof num?.value !== 'number') {
      return {
        value: 0,
        conditionMod: 0,
        slashBaseValue: num?.value ?? 0,
      };
    }
    if (isDebugMode) {
      return {
        value: num?.value ?? 0,
        conditionMod: 0,
        slashBaseValue: num?.value ?? 0,
      };
    }
    const { value, modDelta } = processNumData(
      { num, numId, useMod: true, useCon: true },
      item,
      items
    );
    const slashBaseValue = processNum(
      { num, numId, useMod: false, useCon: true },
      item,
      items
    );
    return { value, conditionMod: modDelta, slashBaseValue };
  }, [num, numId, item, items, isDebugMode]);

  const value = computedNumData.value;
  const conditionMod = computedNumData.conditionMod;

  const updateField = (value, field) => {
    if (!canEdit) {
      return;
    }
    const clonedNum = deepCloneArray(item.has_num);
    let idx = -1;
    for (let i = 0; i < clonedNum.length; i++) {
      const num = clonedNum[i];
      if (Object.keys(num)[0] === numId) {
        idx = i;
      }
    }
    if (idx > -1) {
      clonedNum[idx][numId][field] = field === 'value' ? +value : value;
    }
    update(`items/${itemId}`, 'has_num', clonedNum);
    if (field === 'label') {
      syncTimelineEntryLabels(value);
    }
  };

  const displayMod = (Number(num?.mod) || 0) + conditionMod;

  const slashBaseValue = computedNumData.slashBaseValue;

  const syncTimelineEntryLabels = useCallback(
    newLabel => {
      if (!item?._id) {
        return;
      }
      const previousLabel =
        typeof num?.label === 'string' ? num.label : '';
      const nextLabel = typeof newLabel === 'string' ? newLabel : '';
      if (previousLabel === nextLabel) {
        return;
      }
      const baseItemLabel = item?.label;
      const oldDefaultLabel = buildTimelineEntryLabel(baseItemLabel, previousLabel);
      const newDefaultLabel = buildTimelineEntryLabel(baseItemLabel, nextLabel);

      const allItems = store.getState()?.items || {};
      Object.values(allItems || {}).forEach(targetItem => {
        const timelines = Array.isArray(targetItem?.timelines)
          ? deepCloneArray(targetItem.timelines)
          : null;
        if (!timelines || timelines.length === 0) {
          return;
        }
        let hasChangeForItem = false;
        const updatedTimelines = timelines.map(timelineObj => {
          const timelineId = getId(timelineObj);
          const timelineData = timelineObj?.[timelineId];
          if (!timelineData?.entries) {
            return timelineObj;
          }
          let entriesChanged = false;
          const updatedEntries = timelineData.entries.map(entry => {
            if (entry.itemId !== itemId || entry.numId !== numId) {
              return entry;
            }
            const storedLabel = typeof entry.label === 'string' ? entry.label : '';
            const normalizedStored = storedLabel.trim();
            const hasCustomLabel =
              entry.labelLocked === true ||
              (entry.labelLocked !== true &&
                normalizedStored.length > 0 &&
                normalizedStored !== oldDefaultLabel);
            if (hasCustomLabel) {
              return entry;
            }
            if (
              normalizedStored === newDefaultLabel &&
              entry.labelLocked !== true
            ) {
              return entry;
            }
            entriesChanged = true;
            return {
              ...entry,
              label: newDefaultLabel,
              labelLocked: false,
            };
          });
          if (entriesChanged) {
            hasChangeForItem = true;
            return {
              [timelineId]: {
                ...timelineData,
                entries: updatedEntries,
              },
            };
          }
          return timelineObj;
        });
        if (hasChangeForItem) {
          update(`items/${targetItem._id}`, 'timelines', updatedTimelines);
        }
      });
    },
    [item, itemId, num?.label, numId, store]
  );

  const canDragToTimeline = Boolean(isBoardDragEnabled && num?.timelineDraggable);
  const canDragToTimelineWithEdit = Boolean(canEdit && canDragToTimeline);

  const handleDragPointerEnd = useCallback(() => {
    dragPointerIdRef.current = null;
    endDrag();
    window.removeEventListener('pointerup', handleDragPointerEnd);
    window.removeEventListener('pointercancel', handleDragPointerEnd);
  }, [endDrag]);

  const handleDragPointerDown = useCallback(
    event => {
      if (!canDragToTimelineWithEdit) {
        return;
      }
      event.stopPropagation();
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      const pointerId =
        typeof event.pointerId === 'number' ? event.pointerId : null;
      dragPointerIdRef.current = pointerId;
      startDrag({
        type: 'num',
        itemId,
        numId,
        itemLabel: item?.label || '',
        numLabel: num?.label || '',
        displayLabel:
          item?.label && num?.label
            ? `${item.label} — ${num.label}`
            : num?.label || item?.label || 'Nombre',
        color: fieldInGrid?.color || 'secondary',
        allowTimelineDrop: true,
      });
      window.addEventListener('pointerup', handleDragPointerEnd);
      window.addEventListener('pointercancel', handleDragPointerEnd);
    },
    [
      fieldInGrid?.color,
      handleDragPointerEnd,
      item?.label,
      itemId,
      num?.label,
      numId,
      canDragToTimelineWithEdit,
      startDrag,
    ]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('pointerup', handleDragPointerEnd);
      window.removeEventListener('pointercancel', handleDragPointerEnd);
    };
  }, [handleDragPointerEnd]);

  const isDraggingCurrentNum =
    canDragToTimelineWithEdit &&
    dragData?.type === 'num' &&
    dragData?.numId === numId &&
    dragData?.itemId === itemId;

  const renderDisplayContent = () => {
    if (
      fieldInGrid.displayType === 'big' ||
      fieldInGrid.displayType === 'num' ||
      fieldInGrid.displayType === 'has_num'
    ) {
      return (
        <NumBig
          num={num}
          value={value}
          updateField={updateField}
          displayMod={displayMod}
          slashBaseValue={slashBaseValue}
          canEdit={canEdit}
        />
      );
    }
    if (fieldInGrid.displayType === 'big slash') {
      return (
        <NumBig
          num={num}
          value={value}
          updateField={updateField}
          displayMod={displayMod}
          slashBaseValue={slashBaseValue}
          showSlashValue
          canEdit={canEdit}
        />
      );
    }
    if (
      fieldInGrid.displayType === 'jauge' ||
      fieldInGrid.displayType === 'jauge vertical'
    ) {
      const orientation =
        fieldInGrid.displayType === 'jauge vertical'
          ? 'vertical'
          : 'horizontal';
      return (
        <NumGauge
          num={num}
          numId={numId}
          itemId={itemId}
          value={value}
          orientation={orientation}
          updateField={updateField}
          canEdit={canEdit}
        />
      );
    }
    if (fieldInGrid.displayType === 'little') {
      return (
        <NumLittle
          num={num}
          value={value}
          updateField={updateField}
          displayMod={displayMod}
          canEdit={canEdit}
        />
      );
    }
    if (
      fieldInGrid.displayType === 'little jauge' ||
      fieldInGrid.displayType === 'little jauge vertical'
    ) {
      const orientation =
        fieldInGrid.displayType === 'little jauge vertical'
          ? 'vertical'
          : 'horizontal';
      return (
        <NumLittleGauge
          num={num}
          numId={numId}
          itemId={itemId}
          value={value}
          orientation={orientation}
          updateField={updateField}
          canEdit={canEdit}
        />
      );
    }
    return null;
  };

  const displayContent = renderDisplayContent();
  const tooltipContent =
    typeof num?.tooltip === 'string' && num.tooltip.trim() !== ''
      ? num.tooltip.trim()
      : null;
  const wrappedDisplayContent = <div className='w-full h-full'>{displayContent}</div>;

  const wrappedContent =
    tooltipContent && displayContent && !isMobile ? (
      <div className='w-full h-full'>
        <TooltipHtml
          title={
            <div className='max-w-xs break-words text-sm leading-normal'>
              <TextMd
                str={tooltipContent}
                textId={`${numId}-tooltip`}
                itemId={itemId}
              />
            </div>
          }
          enterDelay={1000}
          enterNextDelay={1000}
        >
          <div className='w-full h-full'>{displayContent}</div>
        </TooltipHtml>
      </div>
    ) : (
      wrappedDisplayContent
    );

  return (
    <>
      {canEdit && (
        <ModalGlobal
          title='Edition'
          idToOpen={modalId}
        >
          <NumSettings
            num={num}
            itemId={itemId}
            numId={numId}
            updateField={updateField}
            value={value}
            modalId={modalId}
          />
        </ModalGlobal>
      )}
      <div className='relative group h-full w-full'>
        {canDragToTimelineWithEdit && (
          <TooltipHtml title='Glisser vers une timeline'>
            <div
              className={`board-grid-no-drag absolute -right-1 top-0 z-10 text-contrast transition opacity-0 group-hover:opacity-90 select-none ${
                isDraggingCurrentNum
                  ? 'opacity-100 cursor-grabbing'
                  : 'cursor-grab'
              }`}
              style={{ right: '1.5rem' }}
              onPointerDown={handleDragPointerDown}
            >
              <FaRegClock />
            </div>
          </TooltipHtml>
        )}
        {wrappedContent}
      </div>
    </>
  );
};
